import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../common/template/i18n";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useAppDispatch } from "../../common/state/hooks";
import {
  type DateRange,
  EmptyState,
  SkeletonCard,
  CreateParcoursGroupModal,
} from "../../features/statsNew/components";
import {
  SessionsPerMonthComparisonChart,
  ActivityHeatmapComparison,
} from "../../features/statsNew/components/charts";
import { useParcoursGroups } from "../../features/statsNew/hooks/useParcoursGroups";
import type { AvailableParcours } from "../../features/statsNew/types/groupTypes";
import { getEntityColor } from "../../features/statsNew/utils/comparisonColors";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import {
  useEnduserRankingTabOptions,
  type EnduserCompareEntityType,
} from "../../features/enduserStats/components/enduserRankingTabOptions";
import {
  fetchEnduserAllSessions,
  getEnduserAllSessions,
  fetchProjectGroupsCompletion,
  getEnduserProjectGroupsCompletion,
} from "../../features/enduserStats/state/enduserStatsSlice";

interface OutletContextType {
  dateRange: DateRange;
}

interface EntityData {
  id: number | string;
  name: string;
  totalSessions: number;
  completedSessions: number;
  completionRate: number;
  averageProgression: number | null;
  averageScore: number | null;
  averageScoreMax: number | null;
  averageDuration: number | null;
  lastSessionDate: Date | null;
  memberCount?: number;
}

const MAX_COMPARE = 3;

// Metric row component with tooltip
const MetricLabel = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div className="flex items-center gap-1.5">
    <span>{label}</span>
    <div className="group relative">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-gray-400 cursor-help hover:text-gray-600 transition-colors"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-64 shadow-lg">
          {tooltip}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
        </div>
      </div>
    </div>
  </div>
);

const EnduserComparateurView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { dateRange } = useOutletContext<OutletContextType>();

  // Get enduser sessions and parcours data
  const allSessions = useSelector(getEnduserAllSessions);
  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);

  const [entityType, setEntityType] = useState<EnduserCompareEntityType>("parcours");
  const enduserRankingTabOptions = useEnduserRankingTabOptions();
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [showCreateParcoursGroupModal, setShowCreateParcoursGroupModal] = useState(false);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

  // Parcours groups hook
  const {
    groups: parcoursGroups,
    createGroup: createParcoursGroup,
    deleteGroup: deleteParcoursGroup,
    canCreateGroup: canCreateParcoursGroup,
  } = useParcoursGroups();

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchEnduserAllSessions({ componentId: "enduserComparateurView" }));
    dispatch(fetchProjectGroupsCompletion({ componentId: "enduserComparateurView" }));
  }, [dispatch]);

  // Track when data has loaded
  useEffect(() => {
    if (allSessions.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [allSessions, isInitialLoad]);

  // Reset selection when entity type changes
  useEffect(() => {
    if (entityType !== "groupes_parcours") {
      setSelectedIds(new Set());
    }
  }, [entityType]);

  // Group sessions by parcours and module
  const sessionsByParcours = useMemo(() => {
    const map = new Map<
      number,
      {
        id: number;
        name: string;
        sessions: typeof allSessions;
        modules: Map<number, { id: number; name: string; sessions: typeof allSessions }>;
      }
    >();

    allSessions.forEach((session) => {
      if (!session.projectGroupId) return;

      if (!map.has(session.projectGroupId)) {
        map.set(session.projectGroupId, {
          id: session.projectGroupId,
          name: session.projectGroupName || `Parcours ${session.projectGroupId}`,
          sessions: [],
          modules: new Map(),
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const parcours = map.get(session.projectGroupId)!;
      parcours.sessions.push(session);

      // Also track by module
      if (session.projectId) {
        if (!parcours.modules.has(session.projectId)) {
          parcours.modules.set(session.projectId, {
            id: session.projectId,
            name: session.projectName || `Module ${session.projectId}`,
            sessions: [],
          });
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        parcours.modules.get(session.projectId)!.sessions.push(session);
      }
    });

    return map;
  }, [allSessions]);

  // Available parcours with stats for the modal
  const availableParcoursWithStats = useMemo((): AvailableParcours[] => {
    const results: AvailableParcours[] = [];

    sessionsByParcours.forEach((parcours) => {
      let totalSessions = 0;
      let lastSessionDate: Date | null = null;

      parcours.sessions.forEach((session) => {
        const sessionDate = new Date(session.createdAt);
        if (dateRange.startDate && sessionDate < dateRange.startDate) return;
        if (dateRange.endDate && sessionDate > dateRange.endDate) return;

        totalSessions++;
        if (!lastSessionDate || sessionDate > lastSessionDate) {
          lastSessionDate = sessionDate;
        }
      });

      // Get completion info from projectGroupsCompletion
      const completionInfo = projectGroupsCompletion.find((p) => p.projectGroupId === parcours.id);

      results.push({
        id: parcours.id,
        name: parcours.name,
        totalSessions,
        completionRate: completionInfo?.progressPercentageOnLatestBuild ?? 0,
        averageScore: null, // Score data not available in enduser sessions
        averageScoreMax: null,
        lastSessionDate,
      });
    });

    return results;
  }, [sessionsByParcours, dateRange, projectGroupsCompletion]);

  // Get all available entities
  const availableEntities = useMemo((): EntityData[] => {
    const entities: EntityData[] = [];

    if (entityType === "parcours") {
      sessionsByParcours.forEach((parcours) => {
        let totalSessions = 0;
        let completedSessions = 0;
        let totalScore = 0;
        let totalScoreMax = 0;
        let scoreCount = 0;
        let totalDuration = 0;
        let durationCount = 0;
        let totalProgression = 0;
        let progressionCount = 0;
        let lastSessionDate: Date | null = null;

        parcours.sessions.forEach((session) => {
          const sessionDate = new Date(session.createdAt);
          if (dateRange.startDate && sessionDate < dateRange.startDate) return;
          if (dateRange.endDate && sessionDate > dateRange.endDate) return;

          totalSessions++;
          if (session.isCompleted) completedSessions++;
          if (session.score != null) {
            totalScore += session.score;
            totalScoreMax += session.scoreMax ?? 0;
            scoreCount++;
          }
          if (session.progressionPercentage != null) {
            totalProgression += session.progressionPercentage;
            progressionCount++;
          }
          if (session.duration && session.duration > 30) {
            totalDuration += session.duration;
            durationCount++;
          }
          if (!lastSessionDate || sessionDate > lastSessionDate) {
            lastSessionDate = sessionDate;
          }
        });

        if (totalSessions > 0) {
          entities.push({
            id: parcours.id,
            name: parcours.name,
            totalSessions,
            completedSessions,
            completionRate: Math.round((completedSessions / totalSessions) * 100),
            averageProgression:
              progressionCount > 0 ? Math.round(totalProgression / progressionCount) : null,
            averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
            averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
            averageDuration: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
            lastSessionDate,
          });
        }
      });
    } else if (entityType === "modules") {
      sessionsByParcours.forEach((parcours) => {
        parcours.modules.forEach((module) => {
          let totalSessions = 0;
          let completedSessions = 0;
          let totalScore = 0;
          let totalScoreMax = 0;
          let scoreCount = 0;
          let totalDuration = 0;
          let durationCount = 0;
          let totalProgression = 0;
          let progressionCount = 0;
          let lastSessionDate: Date | null = null;

          module.sessions.forEach((session) => {
            const sessionDate = new Date(session.createdAt);
            if (dateRange.startDate && sessionDate < dateRange.startDate) return;
            if (dateRange.endDate && sessionDate > dateRange.endDate) return;

            totalSessions++;
            if (session.isCompleted) completedSessions++;
            if (session.score != null) {
              totalScore += session.score;
              totalScoreMax += session.scoreMax ?? 0;
              scoreCount++;
            }
            if (session.progressionPercentage != null) {
              totalProgression += session.progressionPercentage;
              progressionCount++;
            }
            if (session.duration && session.duration > 30) {
              totalDuration += session.duration;
              durationCount++;
            }
            if (!lastSessionDate || sessionDate > lastSessionDate) {
              lastSessionDate = sessionDate;
            }
          });

          if (totalSessions > 0) {
            entities.push({
              id: module.id,
              name: module.name,
              totalSessions,
              completedSessions,
              completionRate: Math.round((completedSessions / totalSessions) * 100),
              averageProgression:
                progressionCount > 0 ? Math.round(totalProgression / progressionCount) : null,
              averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
              averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
              averageDuration: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
              lastSessionDate,
            });
          }
        });
      });
    } else if (entityType === "groupes_parcours") {
      // Calculate stats for each parcours group
      parcoursGroups.forEach((group) => {
        let totalSessions = 0;
        let completedSessions = 0;
        let totalScore = 0;
        let totalScoreMax = 0;
        let scoreCount = 0;
        let totalDuration = 0;
        let durationCount = 0;
        let totalProgression = 0;
        let progressionCount = 0;
        let lastSessionDate: Date | null = null;

        group.parcoursIds.forEach((parcoursId) => {
          const parcours = sessionsByParcours.get(parcoursId);
          if (!parcours) return;

          parcours.sessions.forEach((session) => {
            const sessionDate = new Date(session.createdAt);
            if (dateRange.startDate && sessionDate < dateRange.startDate) return;
            if (dateRange.endDate && sessionDate > dateRange.endDate) return;

            totalSessions++;
            if (session.isCompleted) completedSessions++;
            if (session.score != null) {
              totalScore += session.score;
              totalScoreMax += session.scoreMax ?? 0;
              scoreCount++;
            }
            if (session.progressionPercentage != null) {
              totalProgression += session.progressionPercentage;
              progressionCount++;
            }
            if (session.duration && session.duration > 30) {
              totalDuration += session.duration;
              durationCount++;
            }
            if (!lastSessionDate || sessionDate > lastSessionDate) {
              lastSessionDate = sessionDate;
            }
          });
        });

        entities.push({
          id: group.id,
          name: group.name,
          totalSessions,
          completedSessions,
          completionRate:
            totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0,
          averageProgression:
            progressionCount > 0 ? Math.round(totalProgression / progressionCount) : null,
          averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
          averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
          averageDuration: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
          lastSessionDate,
          memberCount: group.parcoursIds.length,
        });
      });
    }

    return entities.sort((a, b) => b.totalSessions - a.totalSessions);
  }, [sessionsByParcours, dateRange, entityType, parcoursGroups]);

  // Filter by search
  const filteredEntities = useMemo(() => {
    if (!searchValue) return availableEntities;
    const search = searchValue.toLowerCase();
    return availableEntities.filter((e) => e.name.toLowerCase().includes(search));
  }, [availableEntities, searchValue]);

  // Selected entities data
  const selectedEntities = useMemo(() => {
    if (entityType === "groupes_parcours") {
      return availableEntities;
    }
    return availableEntities.filter((e) => selectedIds.has(e.id));
  }, [availableEntities, selectedIds, entityType]);

  // Extract sessions data per entity for charts
  const entitySessionsData = useMemo(() => {
    return selectedEntities.map((entity, index) => {
      const sessions: { date: Date }[] = [];
      const color = getEntityColor(index);

      if (entityType === "parcours") {
        const parcours = sessionsByParcours.get(entity.id as number);
        parcours?.sessions.forEach((session) => {
          const sessionDate = new Date(session.createdAt);
          if (dateRange.startDate && sessionDate < dateRange.startDate) return;
          if (dateRange.endDate && sessionDate > dateRange.endDate) return;
          sessions.push({ date: sessionDate });
        });
      } else if (entityType === "modules") {
        // Search through all parcours to find the module
        sessionsByParcours.forEach((parcours) => {
          const module = parcours.modules.get(entity.id as number);
          module?.sessions.forEach((session) => {
            const sessionDate = new Date(session.createdAt);
            if (dateRange.startDate && sessionDate < dateRange.startDate) return;
            if (dateRange.endDate && sessionDate > dateRange.endDate) return;
            sessions.push({ date: sessionDate });
          });
        });
      } else if (entityType === "groupes_parcours") {
        // Find the parcours group and collect sessions
        const group = parcoursGroups.find((g) => g.id === entity.id);
        if (group) {
          group.parcoursIds.forEach((parcoursId) => {
            const parcours = sessionsByParcours.get(parcoursId);
            parcours?.sessions.forEach((session) => {
              const sessionDate = new Date(session.createdAt);
              if (dateRange.startDate && sessionDate < dateRange.startDate) return;
              if (dateRange.endDate && sessionDate > dateRange.endDate) return;
              sessions.push({ date: sessionDate });
            });
          });
        }
      }

      return {
        entityId: entity.id,
        entityName: entity.name,
        color: color.primary,
        colorGradient: color.gradient,
        primaryColor: color.primary,
        sessions,
      };
    });
  }, [selectedEntities, sessionsByParcours, entityType, dateRange, parcoursGroups]);

  const handleToggleSelect = (id: number | string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else if (newSet.size < MAX_COMPARE) {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return "-";
    const minutes = Math.round(seconds / 60);
    if (minutes < 1) return "< 1 min";
    return `${minutes} min`;
  };

  const formatLastSessionDate = (date: Date | null): string => {
    if (!date) return "-";
    const locale = i18n.language?.startsWith("fr") ? fr : enUS;
    return format(date, "dd MMM yyyy", { locale });
  };

  // Find best/worst for highlighting
  const getBestWorst = (metric: keyof EntityData) => {
    if (selectedEntities.length < 2) return { best: null, worst: null };

    const values = selectedEntities
      .map((e) => ({ id: e.id, value: e[metric] as number | null }))
      .filter((v) => v.value !== null);

    if (values.length < 2) return { best: null, worst: null };

    const sorted = [...values].sort((a, b) => (b.value || 0) - (a.value || 0));
    return { best: sorted[0]?.id, worst: sorted[sorted.length - 1]?.id };
  };

  // Show skeleton during initial load
  if (isInitialLoad) {
    return (
      <div className="flex flex-col h-full">
        <div className="h-10 w-64 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} variant="kpi" />
          ))}
        </div>
      </div>
    );
  }

  // Empty state for non-group types
  if (entityType !== "groupes_parcours" && availableEntities.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-4">
          <SegmentedControl<EnduserCompareEntityType>
            options={enduserRankingTabOptions}
            value={entityType}
            onChange={setEntityType}
            size="lg"
          />
        </div>
        <EmptyState
          icon="chart"
          title={t("enduserStats.comparateur.emptyState.noData")}
          description={t("enduserStats.comparateur.emptyState.noDataDescription")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Sub-tabs */}
      <div className="flex-shrink-0 mb-4">
        <SegmentedControl<EnduserCompareEntityType>
          options={enduserRankingTabOptions}
          value={entityType}
          onChange={setEntityType}
          size="lg"
        />
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left panel - Entity selection or Group management */}
        <div className="w-64 flex-shrink-0 flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden">
          {entityType === "groupes_parcours" ? (
            <>
              {/* Parcours Groups panel header */}
              <div className="p-3 border-b border-gray-200">
                <button
                  onClick={() => setShowCreateParcoursGroupModal(true)}
                  disabled={!canCreateParcoursGroup}
                  className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    canCreateParcoursGroup
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  + {t("enduserStats.comparateur.parcoursGroups.createGroup")}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {parcoursGroups.length}/{MAX_COMPARE}{" "}
                  {t("enduserStats.comparateur.parcoursGroups.groupsCount")}
                </p>
              </div>

              {/* Parcours Groups list */}
              <div className="flex-1 overflow-y-auto p-2">
                {parcoursGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 text-gray-300 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">
                      {t("enduserStats.comparateur.parcoursGroups.noGroups")}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t("enduserStats.comparateur.parcoursGroups.createGroupHint")}
                    </p>
                  </div>
                ) : (
                  parcoursGroups.map((group, index) => {
                    const isExpanded = expandedGroupIds.has(group.id);
                    return (
                      <div
                        key={group.id}
                        className="mb-2 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
                      >
                        <div className="flex items-center p-2">
                          <button
                            onClick={() => {
                              const newSet = new Set(expandedGroupIds);
                              if (isExpanded) {
                                newSet.delete(group.id);
                              } else {
                                newSet.add(group.id);
                              }
                              setExpandedGroupIds(newSet);
                            }}
                            className="mr-1 text-gray-400 hover:text-gray-600"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 transition-transform ${
                                isExpanded ? "rotate-90" : ""
                              }`}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          <div
                            className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                            style={{ backgroundColor: getEntityColor(index).primary }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {group.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {group.parcoursIds.length}{" "}
                              {t("enduserStats.comparateur.parcoursGroups.members")}
                            </p>
                          </div>
                          <button
                            onClick={() => deleteParcoursGroup(group.id)}
                            className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
                            title={t("general.delete")}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="px-3 pb-2 pt-1 border-t border-gray-200 bg-white">
                            {group.parcoursIds.map((parcoursId) => {
                              const parcours = availableParcoursWithStats.find(
                                (p) => p.id === parcoursId,
                              );
                              return (
                                <p
                                  key={parcoursId}
                                  className="text-xs text-gray-600 py-0.5 truncate"
                                >
                                  {parcours?.name || `Parcours ${parcoursId}`}
                                </p>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              {/* Standard entity selection panel */}
              <div className="p-3 border-b border-gray-200">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={t("enduserStats.comparateur.searchPlaceholder")}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    {t("enduserStats.comparateur.selectHint", { max: MAX_COMPARE })}
                  </p>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      selectedIds.size === MAX_COMPARE
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {selectedIds.size}/{MAX_COMPARE}
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                {filteredEntities.map((entity) => {
                  const isSelected = selectedIds.has(entity.id);
                  const isDisabled = !isSelected && selectedIds.size >= MAX_COMPARE;

                  return (
                    <button
                      key={entity.id}
                      onClick={() => handleToggleSelect(entity.id)}
                      disabled={isDisabled}
                      className={`w-full text-left p-2 rounded-lg mb-1 transition-colors ${
                        isSelected
                          ? "bg-green-50 border-2 border-green-500"
                          : isDisabled
                          ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                          : "hover:bg-gray-50 border-2 border-transparent"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{entity.name}</span>
                        {isSelected && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-green-500 flex-shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{entity.totalSessions} sessions</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Right panel - Content area */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          {selectedEntities.length === 0 ? (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <div className="text-center p-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-gray-400 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {entityType === "groupes_parcours" ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  )}
                </svg>
                <p className="text-gray-600 font-medium">
                  {entityType === "groupes_parcours"
                    ? t("enduserStats.comparateur.parcoursGroups.createToCompare")
                    : t("enduserStats.comparateur.selectToCompare")}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {entityType === "groupes_parcours"
                    ? t("enduserStats.comparateur.parcoursGroups.createGroupHint")
                    : t("enduserStats.comparateur.selectHint", { max: MAX_COMPARE })}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Comparison table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table
                  className="w-full table-fixed"
                  role="table"
                  aria-label={t("enduserStats.comparateur.tableLabel")}
                >
                  <thead className="bg-gray-50">
                    <tr role="row">
                      <th
                        className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-32"
                        scope="col"
                      >
                        {t("enduserStats.comparateur.metric")}
                      </th>
                      {selectedEntities.map((entity, index) => (
                        <th
                          key={entity.id}
                          className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-40"
                          scope="col"
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                              <div
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: getEntityColor(index).primary }}
                              />
                              <span className="truncate max-w-[100px]">{entity.name}</span>
                              <button
                                onClick={() =>
                                  entityType === "groupes_parcours"
                                    ? deleteParcoursGroup(entity.id as string)
                                    : handleToggleSelect(entity.id)
                                }
                                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3.5 w-3.5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                            {entityType === "groupes_parcours" &&
                              entity.memberCount !== undefined && (
                                <span className="text-[10px] text-gray-400 font-normal">
                                  {entity.memberCount}{" "}
                                  {t("enduserStats.comparateur.parcoursGroups.members")}
                                </span>
                              )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Sessions */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("enduserStats.comparateur.metrics.sessions")}
                          tooltip={t("enduserStats.comparateur.metricsTooltips.sessions")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("totalSessions");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center text-base font-semibold ${
                              entity.id === best
                                ? "text-green-600 bg-green-50"
                                : entity.id === worst
                                ? "text-red-600 bg-red-50"
                                : "text-gray-900"
                            }`}
                          >
                            {entity.totalSessions}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Completed Sessions */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("enduserStats.comparateur.metrics.completed")}
                          tooltip={t("enduserStats.comparateur.metricsTooltips.completed")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("completedSessions");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center text-base font-semibold ${
                              entity.id === best
                                ? "text-green-600 bg-green-50"
                                : entity.id === worst
                                ? "text-red-600 bg-red-50"
                                : "text-gray-900"
                            }`}
                          >
                            {entity.completedSessions}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Completion Rate */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("enduserStats.comparateur.metrics.completionRate")}
                          tooltip={t("enduserStats.comparateur.metricsTooltips.completionRate")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("completionRate");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center ${
                              entity.id === best
                                ? "bg-green-50"
                                : entity.id === worst
                                ? "bg-red-50"
                                : ""
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <span
                                className={`text-base font-semibold ${
                                  entity.id === best
                                    ? "text-green-600"
                                    : entity.id === worst
                                    ? "text-red-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {entity.completionRate}%
                              </span>
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
                                <div
                                  className={`h-full rounded-full ${
                                    entity.id === best
                                      ? "bg-green-500"
                                      : entity.id === worst
                                      ? "bg-red-500"
                                      : "bg-gray-400"
                                  }`}
                                  style={{ width: `${entity.completionRate}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Average Progression */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("enduserStats.comparateur.metrics.avgProgression")}
                          tooltip={t("enduserStats.comparateur.metricsTooltips.avgProgression")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("averageProgression");
                        const progressionValue = entity.averageProgression ?? 0;
                        const hasProgression = entity.averageProgression !== null;
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center ${
                              entity.id === best
                                ? "bg-green-50"
                                : entity.id === worst
                                ? "bg-red-50"
                                : ""
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <span
                                className={`text-base font-semibold ${
                                  entity.id === best
                                    ? "text-green-600"
                                    : entity.id === worst
                                    ? "text-red-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {hasProgression ? `${progressionValue}%` : "-"}
                              </span>
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    entity.id === best
                                      ? "bg-green-500"
                                      : entity.id === worst
                                      ? "bg-red-500"
                                      : "bg-gray-400"
                                  }`}
                                  style={{ width: `${progressionValue}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Average Score */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("enduserStats.comparateur.metrics.avgScore")}
                          tooltip={t("enduserStats.comparateur.metricsTooltips.avgScore")}
                        />
                      </td>
                      {selectedEntities.map((entity) => {
                        const { best, worst } = getBestWorst("averageScore");
                        return (
                          <td
                            key={entity.id}
                            className={`px-3 py-2 text-center text-base font-semibold ${
                              entity.id === best
                                ? "text-green-600 bg-green-50"
                                : entity.id === worst
                                ? "text-red-600 bg-red-50"
                                : "text-gray-900"
                            }`}
                          >
                            {entity.averageScore !== null && entity.averageScoreMax !== null
                              ? `${entity.averageScore}/${entity.averageScoreMax} pts`
                              : "-"}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Average Duration */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("enduserStats.comparateur.metrics.avgDuration")}
                          tooltip={t("enduserStats.comparateur.metricsTooltips.avgDuration")}
                        />
                      </td>
                      {selectedEntities.map((entity) => (
                        <td
                          key={entity.id}
                          className="px-3 py-2 text-center text-base font-semibold text-gray-900"
                        >
                          {formatDuration(entity.averageDuration)}
                        </td>
                      ))}
                    </tr>

                    {/* Last Session Date */}
                    <tr>
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                        <MetricLabel
                          label={t("enduserStats.comparateur.metrics.lastSessionDate")}
                          tooltip={t("enduserStats.comparateur.metricsTooltips.lastSessionDate")}
                        />
                      </td>
                      {selectedEntities.map((entity) => (
                        <td
                          key={entity.id}
                          className="px-3 py-2 text-center text-base font-semibold text-gray-900"
                        >
                          {formatLastSessionDate(entity.lastSessionDate)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Charts - Activity Heatmaps */}
              {entitySessionsData.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    {t("enduserStats.comparateur.charts.activityHeatmap")}
                  </h3>
                  <ActivityHeatmapComparison entities={entitySessionsData} />
                </div>
              )}

              {/* Charts - Sessions per month */}
              {entitySessionsData.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    {t("enduserStats.comparateur.charts.sessionsPerMonth")}
                  </h3>
                  <div className="h-56">
                    <SessionsPerMonthComparisonChart
                      entities={entitySessionsData}
                      dateRange={dateRange}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Parcours Group Modal */}
      <CreateParcoursGroupModal
        isOpen={showCreateParcoursGroupModal}
        onClose={() => setShowCreateParcoursGroupModal(false)}
        onCreateGroup={createParcoursGroup}
        availableParcours={availableParcoursWithStats}
        existingGroups={parcoursGroups}
      />
    </div>
  );
};

export default EnduserComparateurView;
